import clsx from 'clsx';
import React, { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { useAppSelector } from 'redux/store';
import { setCurrentPage, Vacancy } from 'redux/stores/vacancies';
import { VacancyOverviewFieldsFragment } from 'api/graphql-types';

import { Pagination } from 'components/pagination';
import { Card } from 'components/card';
import { Typography } from 'components/typography';

import styles from './vacancies-overview.module.scss';

const MAX_ITEMS = 9;

export type VacanciesOverviewProps = {
	module: VacancyOverviewFieldsFragment;
	slug: string;
};

const createAbsolutePath = (path: string): string => {
	let cleanedPath = path;

	if (!path.startsWith('/')) {
		cleanedPath = `/${cleanedPath}`;
	}

	if (path.endsWith('/')) {
		cleanedPath = cleanedPath.slice(0, -1);
	}

	return cleanedPath;
};

const VacanciesOverview = ({ module, slug }: VacanciesOverviewProps): React.JSX.Element => {
	const intl = useIntl();
	const overviewRef = useRef<HTMLDivElement>(null);
	const dispatch = useDispatch();
	const { vacancies, totalVacancies, currentPage } = useAppSelector((state) => state.vacancies);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const slicedServerVacancies = vacancies.slice(MAX_ITEMS * currentPage - MAX_ITEMS, MAX_ITEMS * currentPage) || [];

	const onPageChange = async (page: number): Promise<void> => {
		setIsLoading(true);

		if (page !== currentPage) {
			if (typeof window !== 'undefined') {
				const extraOffset = 144;
				const moduleOffset = (overviewRef.current?.getBoundingClientRect().top || 0) + window.pageYOffset - extraOffset;
				window.scrollTo({ top: moduleOffset, left: 0, behavior: 'smooth' });
			}

			await dispatch(setCurrentPage(page));
		}

		setIsLoading(false);
	};

	// eslint-disable-next-line react/no-unstable-nested-components
	const CardBody = (vacancy: Vacancy): React.JSX.Element => {
		return (
			<div>
				<div className={styles.cardBody}>
					<div>{intl.formatMessage({ id: 'modules.vacancies.hours' })}:</div>
					<div>{vacancy.hours || intl.formatMessage({ id: 'modules.vacancies.unknown' })}</div>

					{vacancy.salary && (
						<>
							<div>{intl.formatMessage({ id: 'modules.vacancies.salary' })}:</div>
							<div>{vacancy.salary || intl.formatMessage({ id: 'modules.vacancies.unknown' })}</div>
						</>
					)}

					{!vacancy.salary && !!vacancy.salaryScale && (
						<>
							<div>{intl.formatMessage({ id: 'modules.vacancies.salaryScale' })}:</div>
							<div>{vacancy.salaryScale || intl.formatMessage({ id: 'modules.vacancies.unknown' })}</div>
						</>
					)}

					<div>{intl.formatMessage({ id: 'modules.vacancies.deadline' })}:</div>
					<div>{vacancy.deadline || intl.formatMessage({ id: 'modules.vacancies.unknown' })}</div>
				</div>
			</div>
		);
	};

	return (
		<div ref={overviewRef}>
			<div className={styles.header}>
				{module.title?.value && (
					<Typography tag={module.title?.type || 'h2'} className={styles.title}>
						{module.title.value}
					</Typography>
				)}
				<div className={styles.amountOfResults}>
					{intl.formatMessage({ id: 'global.results' }, { results: totalVacancies })}
				</div>
			</div>
			<div className={clsx(styles.content, isLoading && styles.isLoading)}>
				{slicedServerVacancies.map((vacancy, index) => (
					<Card
						key={index}
						title={vacancy.title}
						slug={`${createAbsolutePath(slug)}/${vacancy.id}`}
						cover={{
							url: vacancy.image || ''
						}}
						bodyText={<CardBody {...vacancy} />}
					/>
				))}
			</div>

			<div className={styles.pagination}>
				<Pagination
					currentPage={currentPage}
					totalPages={Math.ceil(totalVacancies / MAX_ITEMS)}
					onPageChange={onPageChange}
				/>
			</div>
		</div>
	);
};

export default VacanciesOverview;
